import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiEdit, 
  FiCalendar, 
  FiUser, 
  FiPhone, 
  FiMapPin,
  FiCheckCircle,
  FiPackage,
  FiTool,
  FiDollarSign,
  FiFileText,
  FiCamera,
  FiClock,
  FiMaximize2,
  FiX,
  FiExternalLink,
  FiImage
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import siteVisitService from '../services/siteVisitService';
import invoiceService from '../services/invoiceService';
import { useAuth } from '../context/AuthContext';
import { getSocket, joinShopRoom } from '../utils/socket';
import Button from '../components/Button';
import './DetailView.css';

const SiteVisitView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [photoFilter, setPhotoFilter] = useState('all');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [gstSettings, setGstSettings] = useState({
    applyGst: true,
    gstPercent: 18,
  });

  const shopId = user?.shop?._id || user?._id;

  // Real-time socket updates for photo uploads
  useEffect(() => {
    if (!shopId) return;
    joinShopRoom(shopId);

    const socket = getSocket();
    if (!socket) return;

    const handlePhotoAdded = (data) => {
      if (data && String(data.siteVisitId) === String(id)) {
        toast.info(`📸 New ${data.photo?.type || 'site'} photo uploaded by technician!`);
        setVisit((prev) => {
          if (!prev) return prev;
          const currentPhotos = prev.photos || [];
          const exists = currentPhotos.some(
            (p) => (p._id && p._id === data.photo?._id) || (p.url && p.url === data.photo?.url)
          );
          if (exists) return prev;
          return {
            ...prev,
            photos: [data.photo, ...currentPhotos],
          };
        });
      }
    };

    const handleVisitUpdated = (updatedVisit) => {
      if (updatedVisit && String(updatedVisit._id) === String(id)) {
        setVisit(updatedVisit);
      }
    };

    socket.on('site_visit:photo_added', handlePhotoAdded);
    socket.on('site_visit:updated', handleVisitUpdated);

    return () => {
      socket.off('site_visit:photo_added', handlePhotoAdded);
      socket.off('site_visit:updated', handleVisitUpdated);
    };
  }, [shopId, id]);

  // Close lightbox on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedPhoto(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    fetchVisit();
  }, [id]);

  const fetchVisit = async () => {
    try {
      const response = await siteVisitService.getById(id);
      setVisit(response.data);
    } catch (error) {
      toast.error('Failed to fetch site visit details');
      navigate('/site-visits');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const payload = {
        applyGst: gstSettings.applyGst,
        gstPercent:
          typeof gstSettings.gstPercent === 'number'
            ? gstSettings.gstPercent
            : parseFloat(gstSettings.gstPercent) || 18,
      };
      const response = await invoiceService.generate(id, payload);
      const data = response.data || response;
      const invoice = data.data || data;

      if (!invoice || !invoice._id) {
        toast.error('Failed to generate invoice');
        setInvoiceLoading(false);
        return;
      }

      navigate(`/invoices/${invoice._id}`);
    } catch (error) {
      const message =
        error.response?.data?.message || 'Failed to generate invoice';
      toast.error(message);
    } finally {
      setInvoiceLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!visit) {
    return null;
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      scheduled: 'badge-primary',
      in_progress: 'badge-warning',
      completed: 'badge-success',
      cancelled: 'badge-error',
      on_hold: 'badge-gray',
    };
    return `badge ${statusMap[status] || 'badge-gray'}`;
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      low: '#10b981',
      medium: '#1a9dff',
      high: '#f59e0b',
      urgent: '#ef4444',
    };
    return colorMap[priority] || '#6b7280';
  };

  const getPhotoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const formatPhotoDate = (dateVal) => {
    if (!dateVal) return 'Recently';
    try {
      return format(new Date(dateVal), 'MMM dd, yyyy • hh:mm a');
    } catch (_) {
      return 'Recorded on site';
    }
  };

  const photos = visit?.photos || [];
  const filteredPhotos = photoFilter === 'all'
    ? photos
    : photos.filter((p) => (p.type || 'before').toLowerCase() === photoFilter);

  const beforeCount = photos.filter((p) => p.type === 'before').length;
  const afterCount = photos.filter((p) => p.type === 'after').length;
  const diagCount = photos.filter((p) => p.type === 'diagnostic' || p.type === 'other').length;

  return (
    <div className="detail-view-container">
      <div className="detail-header">
        <button onClick={() => navigate('/site-visits')} className="back-button">
          <FiArrowLeft /> Back to List
        </button>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {visit.status === 'completed' && (
            <>
              <div
                style={{
                  padding: '0.5rem 0.75rem',
                  background: 'var(--gray-50)',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                  GST:
                </span>
                <select
                  value={gstSettings.applyGst ? 'yes' : 'no'}
                  onChange={(e) =>
                    setGstSettings((prev) => ({
                      ...prev,
                      applyGst: e.target.value === 'yes',
                    }))
                  }
                  className="input-field"
                  style={{ width: 'auto', minWidth: '90px', padding: '0.25rem 0.5rem' }}
                >
                  <option value="yes">Apply</option>
                  <option value="no">No GST</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={gstSettings.gstPercent}
                  onChange={(e) =>
                    setGstSettings((prev) => ({
                      ...prev,
                      gstPercent: e.target.value,
                    }))
                  }
                  className="input-field"
                  style={{ width: '90px', padding: '0.25rem 0.5rem' }}
                />
                <span style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>%</span>
              </div>
              <Link to={`/site-visits/${id}/payment`}>
                <Button variant="accent" icon={<FiDollarSign />}>
                  Update Payment
                </Button>
              </Link>
              <Button
                variant="secondary"
                icon={<FiFileText />}
                loading={invoiceLoading}
                onClick={handleInvoice}
              >
                Invoice
              </Button>
            </>
          )}
          {visit.status !== 'cancelled' && (
            <Link to={`/site-visits/${id}/edit`}>
              <Button variant="secondary" icon={<FiEdit />}>
                Edit Visit &amp; Items
              </Button>
            </Link>
          )}
          {visit.status !== 'completed' && visit.status !== 'cancelled' && (
            <Link to={`/site-visits/${id}/complete`}>
              <Button variant="primary" icon={<FiCheckCircle />}>
                Complete Visit
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="detail-card">
        <div 
          className="detail-hero" 
          style={{ background: `linear-gradient(135deg, ${getPriorityColor(visit.priority)} 0%, ${getPriorityColor(visit.priority)}dd 100%)` }}
        >
          <div className="hero-avatar">
            <FiCalendar />
          </div>
          <div className="hero-info">
            <h1 className="hero-title">{visit.customer.name}</h1>
            <p className="hero-subtitle">Ticket: #{visit.ticketNumber}</p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <span className={getStatusBadge(visit.status)}>
                {visit.status.replace('_', ' ')}
              </span>
              <span className="badge badge-gray">
                {visit.priority.toUpperCase()} Priority
              </span>
            </div>
          </div>
        </div>

        <div className="detail-sections">
          <div className="detail-section">
            <h3 className="section-title">Complaint</h3>
            <p style={{ fontSize: '1.125rem', color: 'var(--gray-700)', lineHeight: '1.7' }}>
              {visit.complaint}
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
              <span className="tag">Service: {visit.serviceType}</span>
            </div>
          </div>

          <div className="detail-section">
            <h3 className="section-title">Customer Information</h3>
            <div className="customer-info-grid">
              <div className="info-item">
                <FiUser className="info-icon" />
                <div>
                  <span className="info-label">Name</span>
                  <span className="info-value">{visit.customer.name}</span>
                </div>
              </div>
              <div className="info-item">
                <FiPhone className="info-icon" />
                <div>
                  <span className="info-label">Phone</span>
                  <span className="info-value">{visit.customer.phone}</span>
                </div>
              </div>
              {visit.customer.email && (
                <div className="info-item">
                  <FiPhone className="info-icon" />
                  <div>
                    <span className="info-label">Email</span>
                    <span className="info-value">{visit.customer.email}</span>
                  </div>
                </div>
              )}
              {visit.customer.address?.city && (
                <div className="info-item">
                  <FiMapPin className="info-icon" />
                  <div>
                    <span className="info-label">Location</span>
                    <span className="info-value">
                      {visit.customer.address.city}, {visit.customer.address.state}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3 className="section-title">Assignment & Schedule</h3>
            <div className="info-grid">
              <div className="info-item">
                <FiUser className="info-icon" />
                <div>
                  <span className="info-label">Technician</span>
                  <span className="info-value">
                    {visit.assignedTechnician?.name || 'Not assigned'}
                  </span>
                </div>
              </div>
              {visit.assignedHelper && (
                <div className="info-item">
                  <FiUser className="info-icon" />
                  <div>
                    <span className="info-label">Helper</span>
                    <span className="info-value">{visit.assignedHelper.name}</span>
                  </div>
                </div>
              )}
              <div className="info-item">
                <FiCalendar className="info-icon" />
                <div>
                  <span className="info-label">Scheduled</span>
                  <span className="info-value">
                    {format(new Date(visit.scheduledDate), 'MMM dd, yyyy - hh:mm a')}
                  </span>
                </div>
              </div>
              {visit.completionDate && (
                <div className="info-item">
                  <FiCheckCircle className="info-icon" />
                  <div>
                    <span className="info-label">Completed</span>
                    <span className="info-value">
                      {format(new Date(visit.completionDate), 'MMM dd, yyyy - hh:mm a')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Site Photos Gallery Section */}
          <div className="detail-section">
            <div className="photos-section-header">
              <h3 className="photos-section-title">
                <FiCamera style={{ color: 'var(--primary-600, #2563eb)' }} />
                <span>Site Photos</span>
                <span className="photos-count-badge">
                  {photos.length} {photos.length === 1 ? 'Photo' : 'Photos'}
                </span>
              </h3>

              {photos.length > 0 && (
                <div className="photo-filter-pills">
                  <button
                    type="button"
                    className={`photo-pill-btn ${photoFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setPhotoFilter('all')}
                  >
                    All ({photos.length})
                  </button>
                  <button
                    type="button"
                    className={`photo-pill-btn ${photoFilter === 'before' ? 'active' : ''}`}
                    onClick={() => setPhotoFilter('before')}
                  >
                    Before Service ({beforeCount})
                  </button>
                  <button
                    type="button"
                    className={`photo-pill-btn ${photoFilter === 'after' ? 'active' : ''}`}
                    onClick={() => setPhotoFilter('after')}
                  >
                    After Service ({afterCount})
                  </button>
                  {diagCount > 0 && (
                    <button
                      type="button"
                      className={`photo-pill-btn ${photoFilter === 'diagnostic' ? 'active' : ''}`}
                      onClick={() => setPhotoFilter('diagnostic')}
                    >
                      Diagnostic / Other ({diagCount})
                    </button>
                  )}
                </div>
              )}
            </div>

            {photos.length === 0 ? (
              <div className="no-site-photos-box">
                <div className="no-site-photos-icon">
                  <FiCamera />
                </div>
                <h4 className="no-site-photos-title">No Site Photos Uploaded Yet</h4>
                <p className="no-site-photos-desc">
                  When the assigned technician captures Before &amp; After photos on site using the HVAC Technician mobile app, they will automatically appear here in real time.
                </p>
              </div>
            ) : filteredPhotos.length === 0 ? (
              <div className="no-site-photos-box" style={{ padding: '2rem 1rem' }}>
                <p className="no-site-photos-desc">
                  No <strong>{photoFilter}</strong> photos attached for this visit.
                </p>
              </div>
            ) : (
              <div className="site-photos-grid">
                {filteredPhotos.map((photo, index) => {
                  const pType = (photo.type || 'before').toLowerCase();
                  const badgeClass =
                    pType === 'before'
                      ? 'badge-type-before'
                      : pType === 'after'
                      ? 'badge-type-after'
                      : pType === 'diagnostic'
                      ? 'badge-type-diagnostic'
                      : 'badge-type-other';

                  const fullUrl = getPhotoUrl(photo.url);

                  return (
                    <div key={photo._id || index} className="site-photo-card">
                      <div
                        className="site-photo-thumb-wrap"
                        onClick={() => setSelectedPhoto(photo)}
                        title="Click to zoom photo"
                      >
                        <span className={`site-photo-badge ${badgeClass}`}>
                          {photo.type || 'before'}
                        </span>
                        <img
                          src={fullUrl}
                          alt={photo.caption || `${photo.type || 'site'} photo`}
                          className="site-photo-img"
                          loading="lazy"
                        />
                        <div className="site-photo-zoom-hint">
                          <FiMaximize2 />
                        </div>
                      </div>

                      <div className="site-photo-content">
                        <div className="site-photo-time">
                          <FiClock />
                          <span>Uploaded: {formatPhotoDate(photo.takenAt)}</span>
                        </div>
                        {photo.caption ? (
                          <p className="site-photo-caption">{photo.caption}</p>
                        ) : (
                          <p className="site-photo-empty-caption">No notes provided</p>
                        )}
                        <div className="site-photo-card-footer">
                          <button
                            type="button"
                            className="site-photo-link-btn"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <FiMaximize2 /> View Full Size
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {(visit.itemsTaken?.length > 0 || visit.toolsTaken?.length > 0) && (
            <div className="detail-section">
              <h3 className="section-title">Items & Tools Taken</h3>
              <div className="items-tools-section">
                {visit.itemsTaken?.length > 0 && (
                  <div className="items-list">
                    <h4><FiPackage /> Items ({visit.itemsTaken.length})</h4>
                    {visit.itemsTaken.map((item, idx) => (
                      <div key={idx} className="item-row">
                        <span className="item-name">{item.itemName}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                          <span className="item-qty" style={{ background: '#e3f2fd', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontSize: '0.875rem' }}>
                            Taken: {item.quantity}
                          </span>
                          {item.returnQuantity > 0 && (
                            <span className="item-qty" style={{ background: '#f3e5f5', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontSize: '0.875rem' }}>
                              Returned: {item.returnQuantity}
                            </span>
                          )}
                          {item.usedQuantity > 0 && (
                            <span className="item-qty" style={{ background: '#fff3e0', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontSize: '0.875rem' }}>
                              Used: {item.usedQuantity}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {visit.toolsTaken?.length > 0 && (
                  <div className="tools-list">
                    <h4><FiTool /> Tools ({visit.toolsTaken.length})</h4>
                    {visit.toolsTaken.map((tool, idx) => (
                      <div key={idx} className="item-row">
                        <span className="item-name">{tool.toolName}</span>
                        {tool.returned && <span className="returned-badge">Returned</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {visit.totalCost && (
            <div className="detail-section">
              <h3 className="section-title">Cost Breakdown</h3>
              <div className="cost-breakdown">
                <div className="cost-item">
                  <span className="cost-label">Labor Cost</span>
                  <span className="cost-amount">
                    ₹{visit.totalCost.laborCost?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="cost-item">
                  <span className="cost-label">Material Cost</span>
                  <span className="cost-amount">
                    ₹{visit.totalCost.materialCost?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="cost-item">
                  <span className="cost-label">Total Amount</span>
                  <span className="cost-amount total">
                    ₹{visit.totalCost.totalAmount?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <span className={`badge ${visit.paymentStatus === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                  Payment: {visit.paymentStatus?.toUpperCase() || 'PENDING'}
                </span>
              </div>
            </div>
          )}

          {visit.workDescription && (
            <div className="detail-section">
              <h3 className="section-title">Work Description</h3>
              <p style={{ fontSize: '1rem', color: 'var(--gray-700)', lineHeight: '1.7' }}>
                {visit.workDescription}
              </p>
            </div>
          )}

          {visit.notes && (
            <div className="detail-section">
              <h3 className="section-title">Notes</h3>
              <p style={{ fontSize: '1rem', color: 'var(--gray-600)', lineHeight: '1.7' }}>
                {visit.notes}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox / Full-screen Photo Modal */}
      {selectedPhoto && (
        <div
          className="photo-lightbox-backdrop"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="photo-lightbox-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="photo-lightbox-header">
              <div className="photo-lightbox-header-left">
                <span
                  className={`site-photo-badge ${
                    (selectedPhoto.type || 'before') === 'before'
                      ? 'badge-type-before'
                      : (selectedPhoto.type || 'before') === 'after'
                      ? 'badge-type-after'
                      : 'badge-type-diagnostic'
                  }`}
                  style={{ position: 'static' }}
                >
                  {(selectedPhoto.type || 'before').toUpperCase()}
                </span>
                <span style={{ fontSize: '0.88rem', opacity: 0.9 }}>
                  Uploaded: {formatPhotoDate(selectedPhoto.takenAt)}
                </span>
              </div>
              <button
                type="button"
                className="photo-lightbox-close-btn"
                onClick={() => setSelectedPhoto(null)}
                title="Close (Esc)"
              >
                <FiX />
              </button>
            </div>

            <div className="photo-lightbox-image-wrap">
              <img
                src={getPhotoUrl(selectedPhoto.url)}
                alt={selectedPhoto.caption || 'Site inspection photo'}
              />
            </div>

            <div className="photo-lightbox-footer">
              <p className="photo-lightbox-caption">
                {selectedPhoto.caption || 'Site visit photo captured by technician.'}
              </p>
              <a
                href={getPhotoUrl(selectedPhoto.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="photo-lightbox-open-external"
              >
                <FiExternalLink /> Open High-Res Original
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteVisitView;
